/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { EPGImageFormat, EPGImageSliderItem } from '../epg-image-slider/epg-image-slider.component';
import { IPageInfo } from '@iharbeck/ngx-virtual-scroller';
import {
  CustomDateFormatService,
  DateFormat
} from '../../../services/custom-date-format/custom-date-format.service';
import { SearchModel } from '../../../models/search.model';
import { TaskService } from '../../../services/task.service';
import { WorkflowService } from '../../../services/workflow.service';
import {
  TaskDataArray,
  TaskDataImage,
  TaskDataImageUrl,
  TaskDataObject,
  TaskDatumModel,
  TaskDatumType,
  TaskModel
} from '../../../models/task.model';
import { LocationModel, WorkflowItemType } from '../../../models/record/record-base.model';
import { attachCompanyNameToUrl, hasUUIDInUrl } from '../functions';
import { CameraRecordData } from '../../../models/record/items/camera-record.model';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { EmployeeService } from '../../../services/employee.service';
import { faCamera, faSearchPlus, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { faXmarkLarge } from '@fortawesome/pro-solid-svg-icons';
import { TaskStateLocationData } from '../../home/task-home/task/task-detail/task-detail.component';
import { ExifMetadataService } from '../exif/exif-metadata.service';
import { UI_EVENT_DEBOUNCE_TIME_IN_MS } from '../constants';

export interface GalleryFilter {
  // searchText: string; // We don't have a description; this has to be discussed
  from: Date;
  to: Date;
  workflowId: string;
  employeeId: string | string[];
}

interface GalleryImage extends EPGImageSliderItem {
  task?: string;
  taskId: string;
  workflow?: string;
  workflowId: string;
  executedBy: string[];
  timestamp?: string;
}

type ExifData = Record<string, any> | undefined;

@Component({
  selector: 'oneapp-task-image-grid',
  templateUrl: './oneapp-task-image-grid.component.html',
  styleUrls: ['./oneapp-task-image-grid.component.scss'],
  standalone: false
})
export class OneappTaskImageGridComponent implements OnInit, OnChanges, OnDestroy {
  @Input() filter!: GalleryFilter;
  @Input() enableLink: boolean = true;

  @Output() imagesLoaded = new EventEmitter<boolean>();

  readonly faSearchPlus = faSearchPlus;
  readonly faXmarkLarge = faXmarkLarge;
  readonly faSpinnerThird = faSpinnerThird;

  readonly DateFormat = DateFormat;

  readonly destroy$ = new Subject<void>();
  readonly changeFilter$ = new Subject<void>();

  imageReferences: GalleryImage[] = [];
  selectedImage: GalleryImage[] = [];

  isFetching = false;

  readonly batchSize = 25;
  readonly initialTargetAmount = 50;

  fetchedTasksAmount = 0;
  targetImageAmount = this.initialTargetAmount;

  constructor(
    public customDateFormatService: CustomDateFormatService,
    public workflowService: WorkflowService,
    public taskService: TaskService,
    public employeeService: EmployeeService,
    private exifMetadataService: ExifMetadataService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit(): Promise<void> {
    this.taskService.tasksChanged
      .pipe(debounceTime(UI_EVENT_DEBOUNCE_TIME_IN_MS), takeUntil(this.destroy$))
      .subscribe((tasks) => {
        this.processTasks(tasks);
        this.cdr.detectChanges();
      });

    this.changeFilter$
      .pipe(debounceTime(UI_EVENT_DEBOUNCE_TIME_IN_MS), takeUntil(this.destroy$))
      .subscribe(() => {
        this.fetchTaskData();
      });

    if (this.filter) {
      this.changeFilter$.next();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filter && !changes.filter.isFirstChange()) {
      this.selectedImage = [];
      this.imageReferences = [];
      this.fetchedTasksAmount = 0;
      this.targetImageAmount = this.initialTargetAmount;
      this.changeFilter$.next();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onScroll(event: IPageInfo): void {
    // Try to fetch more images, if only batchSize amount of images remains
    if (
      !this.isFetching &&
      this.imageReferences.length > 0 &&
      event.endIndex >= this.imageReferences.length - this.batchSize
    ) {
      this.targetImageAmount = this.imageReferences.length + this.batchSize;
      this.fetchTaskData();
    }
  }

  async onImageSelected(image: GalleryImage) {
    if (image.workflowId) {
      image.workflow = this.workflowService.getWorkflowNameFromId(image.workflowId);
    }
    this.selectedImage = [image];
  }

  fetchTaskData(): void {
    // Don't fetch any more data, if we reached the desired amount of images
    if (this.imageReferences.length >= this.targetImageAmount) {
      this.isFetching = false;
      return;
    }

    this.isFetching = true;

    const searchCriteria = this.getSearchCriteria(this.filter);
    const searchModel = new SearchModel(
      searchCriteria,
      this.fetchedTasksAmount,
      this.batchSize,
      '-createdAt',
      'name workflow_id executed_by assigned_to assigned_to_employee_or_employeegroup createdAt output record task_id'
    );

    this.taskService.searchTask(searchModel, true);
  }

  async processTasks(tasks: TaskModel[]): Promise<void> {
    this.fetchedTasksAmount += tasks.length;

    // Process all tasks in parallel for faster loading
    const imageProcessingPromises: Promise<void>[] = [];
    let tasksWithImages = 0;
    let tasksWithRecords = 0;
    let tasksWithOutput = 0;

    for (let task of tasks) {
      let hasProcessedImages = false;

      if (task.record && task.record.length > 0) {
        tasksWithRecords++;
        for (let record of task.record) {
          // show only images from camera
          if (this.isCameraItem(record.itemType)) {
            imageProcessingPromises.push(
              this.handleRecordImages(
                (record.itemData as CameraRecordData).images ?? [],
                this.imageReferences,
                task
              )
            );
            hasProcessedImages = true;
            tasksWithImages++;
          }
        }
      }

      if (!hasProcessedImages && task.output != null) {
        tasksWithOutput++;
        for (let [key, value] of task.output.nodes) {
          imageProcessingPromises.push(
            this.extractImageReferences(value, this.imageReferences, task)
          );
        }
      }
    }

    // Wait for all images to be processed in parallel
    await Promise.all(imageProcessingPromises);

    // We don't know the total amount of tasks available.
    // Therefore, if we received the requested amount, try to fetch more.
    if (tasks.length == this.batchSize) {
      this.fetchTaskData();
    } else {
      this.isFetching = false;
    }

    // Emit whether we have images to display
    this.imagesLoaded.emit(this.imageReferences.length > 0);
  }

  getSearchCriteria(filter: GalleryFilter): {} {
    let criteria = {};
    let andBlock = [];

    andBlock.push({ status: { $not: { $eq: 'DELETED' } } });

    if (filter.workflowId.length > 0) {
      andBlock.push({ workflow_id: filter.workflowId });
    } else {
    }

    if (filter.employeeId) {
      const employeeIds = Array.isArray(filter.employeeId)
        ? filter.employeeId
        : [filter.employeeId];

      const normalizedIds = employeeIds
        .map((id) => id?.toString().trim())
        .filter((id): id is string => !!id);

      if (normalizedIds.length === 1) {
        andBlock.push({ executed_by: normalizedIds[0] });
      } else if (normalizedIds.length > 1) {
        andBlock.push({ executed_by: { $in: normalizedIds } });
      }
    }

    const fromISO = filter.from.toISOString();
    const toISO = filter.to.toISOString();
    andBlock.push({
      createdAt: {
        $gte: fromISO,
        $lte: toISO
      }
    });

    criteria = { $and: andBlock };
    return criteria;
  }

  appendReturnTypeForBlobServiceUrl(url: string): string {
    if (!url || typeof url !== 'string') {
      return url || '';
    }

    if (url.includes('returnType=')) {
      return url;
    }

    if (hasUUIDInUrl(url)) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}returnType=direct`;
    }

    return url;
  }

  async handleRecordImages(
    images: EPGImageSliderItem[],
    imageReferences: GalleryImage[],
    task: TaskModel
  ): Promise<void> {
    // Process all images in parallel for faster loading
    const enrichedImages = await Promise.all(
      images.map(async (image) => {
        if (image.imageFormat == EPGImageFormat.URL) {
          image.image = this.appendReturnTypeForBlobServiceUrl(image.image);
        }
        const enrichedImage = await this.ensureExifData(image);
        return {
          ...enrichedImage,
          description: task.description,
          task: task.name,
          executedBy: this.convertEmployeeIdsToNameOrUsername(task.executedBy),
          timestamp: task.createdAt,
          workflow: '',
          taskId: task.taskId,
          workflowId: task.workflowId
        };
      })
    );

    // Add images one by one with duplicate check
    for (const enrichedImage of enrichedImages) {
      this.addUniqueImageReference(enrichedImage, imageReferences, 'camera-record');
    }
  }

  isCameraItem(itemType: WorkflowItemType): boolean {
    return itemType == WorkflowItemType.CAMERA;
  }

  private async extractImageReferences(
    propertyValue: TaskDatumModel,
    imageReferences: GalleryImage[],
    task: TaskModel
  ): Promise<void> {
    const type = propertyValue['type'];

    switch (type) {
      case TaskDatumType.IMAGE_JPEG:
      case TaskDatumType.IMAGE_PNG: {
        const inlineImage = await this.processImageData(propertyValue, task, '');
        this.addUniqueImageReference(inlineImage, imageReferences, 'task-output');
        break;
      }
      case TaskDatumType.IMAGE_URL: {
        const urlImage = await this.processImageUrl(propertyValue, task, '');
        this.addUniqueImageReference(urlImage, imageReferences, 'task-output');
        break;
      }
      case TaskDatumType.EXTENDED_IMAGE: {
        const image = propertyValue['value'].image;
        const description = propertyValue['value'].description;
        const extendedImage = await this.processImageReference(image, task, description.value);
        this.addUniqueImageReference(extendedImage, imageReferences, 'task-output');
        break;
      }
      case TaskDatumType.OBJECT:
        const imageData = propertyValue['value'].get('image');
        const descriptionData = propertyValue['value'].get('description');
        if (imageData && descriptionData) {
          const objectImage = await this.processImageReference(
            imageData[0],
            task,
            descriptionData[0].value
          );
          this.addUniqueImageReference(objectImage, imageReferences, 'task-output');
        } else {
          for (let [key, value] of (propertyValue as TaskDataObject)['value']) {
            this.extractImageReferences(value, imageReferences, task);
          }
        }
        break;
      case TaskDatumType.ARRAY:
        (propertyValue as TaskDataArray)['value'].forEach((element) => {
          this.extractImageReferences(element, imageReferences, task);
        });
        break;
    }
  }

  async processImageReference(
    image: TaskDataImage | TaskDataImageUrl,
    task: TaskModel,
    description: string
  ): Promise<GalleryImage> {
    if (image.type === TaskDatumType.IMAGE_URL) {
      return await this.processImageUrl(image, task, description);
    } else {
      return this.processImageData(image, task, description);
    }
  }

  async processImageData(
    image: TaskDataImage,
    task: TaskModel,
    description: string
  ): Promise<GalleryImage> {
    let mimeType: string;
    if (image.type === TaskDatumType.IMAGE_PNG) mimeType = 'image/png';
    else mimeType = 'image/jpeg';
    let imageData = 'data:' + mimeType + ';base64,' + image.value;

    return await this.ensureExifData({
      image: imageData,
      imageFormat: EPGImageFormat.BASE64,
      thumbImage: imageData,
      title: '',
      description: description,
      task: task.name,
      executedBy: this.convertEmployeeIdsToNameOrUsername(task.executedBy),
      timestamp: task.createdAt,
      workflow: '',
      rotationValue: 0,
      scaleValue: 1,
      taskId: task.taskId,
      workflowId: task.workflowId
    });
  }

  async processImageUrl(
    image: TaskDataImageUrl,
    task: TaskModel,
    description: string
  ): Promise<GalleryImage> {
    let imageUrl = attachCompanyNameToUrl(image.value);
    imageUrl = this.appendReturnTypeForBlobServiceUrl(imageUrl);
    return await this.ensureExifData({
      image: imageUrl,
      imageFormat: EPGImageFormat.URL,
      thumbImage: image.value,
      title: '',
      description: description,
      task: task.name,
      executedBy: this.convertEmployeeIdsToNameOrUsername(task.executedBy),
      timestamp: task.createdAt,
      workflow: '',
      rotationValue: 0,
      scaleValue: 1,
      taskId: task.taskId,
      workflowId: task.workflowId
    });
  }

  private async ensureExifData<T extends EPGImageSliderItem>(image: T): Promise<T> {
    if (image.exifData) {
      return image;
    }

    const exifData = await this.loadExifData(image.image, image.imageFormat);
    return {
      ...image,
      exifData
    };
  }

  private async loadExifData(imageSource: string, format: EPGImageFormat): Promise<ExifData> {
    try {
      if (format === EPGImageFormat.BASE64) {
        return await this.exifMetadataService.readFromBase64(imageSource);
      }

      if (format === EPGImageFormat.URL) {
        return await this.exifMetadataService.readFromUrl(imageSource);
      }
    } catch (error) {
      return undefined;
    }

    return undefined;
  }

  convertExifDateToDate(exifDate: string): string {
    const match = exifDate.match(/^(\d{4}):(\d{2}):(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
    if (!match) {
      return '';
    }

    const [, /* ignored fully matched string */ year, month, day, hour, minute, second] = match;
    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );

    return date.toISOString();
  }

  convertExifGpsToLocationTag(exifLatitude: any, exifLongitude: any): TaskStateLocationData[] {
    return [
      new TaskStateLocationData(
        WorkflowItemType.CAMERA,
        '',
        faCamera,
        new LocationModel(exifLatitude, exifLongitude)
      )
    ];
  }

  private convertEmployeeIdsToNameOrUsername(employeeIds: string[]): string[] {
    return employeeIds.map(
      (empId) =>
        this.employeeService.getEmployeeNameFromID(empId) ??
        this.employeeService.getEmployeeUserNameFromID(empId) ??
        empId
    );
  }

  private addUniqueImageReference(
    imageRef: GalleryImage,
    imageReferences: GalleryImage[],
    source: 'camera-record' | 'task-output'
  ): void {
    const isDuplicate = imageReferences.some(
      (existing) => existing.image === imageRef.image && existing.taskId === imageRef.taskId
    );
    if (isDuplicate) {
      return;
    }

    imageReferences.push(imageRef);
  }

  onImageClick(image: GalleryImage) {
    image.title = image.description;
  }

  onLightBoxClose(image: GalleryImage) {
    image.title = '';
  }

  getWorkflowRoute(id: String): string {
    return `home/workflow/${id}`;
  }

  getTaskRoute(id: String): string {
    return `home/tasks/activities/${id}`;
  }
}
